import {action, computed, makeAutoObservable, makeObservable, observable, runInAction, set} from "mobx";
import {interpret} from "xstate";
import {FetchMachine} from "../components/machines/FetchMachine";
import {getPlatform} from "../utils/StringUtilities";
import {sourceTypesEnum} from "../utils/SourceUtils";
import {isFacebookImageExpired} from "../utils/FacebookUtils";
import WatsonApi from "../backends/WatsonApi";
import {ExecutionEnvironment} from "./InterfaceStore";
import {getCdnUrl} from "../utils/SchoolBlocksUtilities";

interface IReactiveContentItem extends ContentItem {
    update: (obj: { [v: string]: any }) => void,
    image: string,
}

export function isEvent(item: EventContentItem | ContentItem): item is EventContentItem {
    return (item as EventContentItem).is_event === true;
}

export function isMedia(item: MediaContentItem | ContentItem): item is MediaContentItem {
    return (item as MediaContentItem).is_media === true;
}

export function isFile(item: FileContentItem | ContentItem): item is FileContentItem {
    return (item as FileContentItem).is_file === true;
}

export function isNews(item: ContentItem): item is NewsContentItem {
    return (item).is_news === true;
}

export type IReactiveNewsContentItem = IReactiveContentItem & (NewsContentItem | MediaContentItem | FileContentItem);
export type IReactiveEventContentItem = IReactiveContentItem & EventContentItem;
export type IReactiveFileContentItem = IReactiveContentItem & FileContentItem;
export type IReactiveMediaContentItem = IReactiveContentItem & MediaContentItem;
export type SchoolFeedContentItem = IReactiveEventContentItem | IReactiveNewsContentItem | IReactiveFileContentItem | IReactiveMediaContentItem;

export class ReactiveContentItem {
    // see https://github.com/mobxjs/mobx/issues/2486, should be able to remove this after upgrading to babel@7.12.0
    organization = undefined;
    item: NewsContentItem | MediaContentItem | FileContentItem | EventContentItem;

    constructor(item: NewsContentItem | MediaContentItem | FileContentItem | EventContentItem) {
        this.item = item;

        const obj = {
            update: action,
            image: computed,
        }
        for (const key in item) {
            if (item.hasOwnProperty(key)) {
                this[key] = item[key];
                obj[key] = observable;
            }
        }

        makeObservable(this, obj)
    }

    get image() {
        return getItemImage(this.item);
    }

    get altText() {
        return getAltText(this.item);
    }

    update(obj) {
        for (const key in obj) {
            if (obj.hasOwnProperty(key)) {
                this[key] = obj[key];
            }
        }
    }
}

function isNotEmpty(item) {
    return item.title || item.image || item.description;
}

export function getAltText(item: ContentItem) {
    const isImage = /photo/.test(item.type);
    let text;

    if (isNews(item)) {
        text = item.json_data.news.cover_image_alt_text;
    } else if (isEvent(item)) {
        text = item.json_data.event.full_picture_alt_text;
    } else if (isMedia(item) && isImage && item.json_data.images && item.json_data.images.length > 0) {
        text = item.json_data.images[item.json_data.images.length - 1].alt_text;
    } else if (isMedia(item) && item.json_data.videos) {
        text = item.json_data.videos.videos_list[0].alt_text;
    } else if (isFile(item) && item.json_data.file.cover_image_url) {
        text = item.json_data.file.cover_image_alt_text;
    }

    if (!text) {
        if (item.is_custom) {
            text = item.title;
        } else {
            text = `Image from ${getPlatform(item.type)}`;
        }
    }

    return text;
}

/**
 * @param item -
 * @param useSourceImageDefault - default to item's content_source image if item doesn't have its own image
 */
export function getItemImage(item: ContentItem, useSourceImageDefault: boolean = true) {
    const isImage = /photo/.test(item.type);
    const platform = getPlatform(item.type);

    let image: string | null  = null;

    // first try to get the image from the content item json_data
    if (isNews(item) && item.json_data.news.cover_image_url) {
        image = item.json_data.news.cover_image_url;
    } else if (isEvent(item) && item.json_data.event && item.json_data.event.full_picture) {
        image = item.json_data.event.full_picture;
    } else if (isMedia(item) && isImage && item.json_data.images && item.json_data.images.length > 0) {
        image = item.json_data.images[item.json_data.images.length - 1].raw_url;
    } else if (isMedia(item) && item.json_data.videos) {
        const placeholder = require("../assets/images/placeholders/placeholder-video.png");
        image = item.json_data.videos.cover_image_url || placeholder;
    } else if (isFile(item) && item.json_data.file.cover_image_url) {
        image = item.json_data.file.cover_image_url;
    }

    // make sure image URL isn't expired if it's from Facebook - if it is, we need to continue onward
    if (platform === sourceTypesEnum.FACEBOOK && isFacebookImageExpired(image)) {
        image = null;
    }

    // if we still don't have an image, try to extract from content source, making sure that if it's from Facebook it isn't expired
    if (
        !image &&
        useSourceImageDefault &&
        item.source &&
        item.source.account_image &&
        !(
            platform === sourceTypesEnum.FACEBOOK &&
            isFacebookImageExpired(item.source.account_image)
        ) &&
        platform !== sourceTypesEnum.GOOGLE_DRIVE
    ) {
        if (platform === sourceTypesEnum.TWITTER) {
            // use larger version of profile picture
            image = item.source.account_image.replace("_normal", "");
        } else {
            image = item.source.account_image;
        }
    }

    if (item.is_custom) {
        image = getCdnUrl(image || "");
    }

    return image;
}

/*
switched from managing content in React state to a mobx store primarily because needed a way to easily observe and react to
item property changes - i.e. the published value. React doesn't provide a straightforward way to do that since object references
do not change. Also added benefit of being able to manage fetching and content from a single class instance rather than using
multiple states that need to be combined inside the component.
*/
export class SchoolFeedStore {
    // news contains is_news, is_media, and is_file.
    allContent: SchoolFeedContentItem[] = [];
    fetchStatus = FetchMachine.initialState.value;

    service = interpret(FetchMachine).onTransition(current => {
        runInAction(() => this.fetchStatus = current.value);
    });

    constructor() {
        makeAutoObservable(this);

        if (ExecutionEnvironment.canUseDOM) {
            // this leaks memory, so very important this doesn't get run on the server!
            this.service.start();
        }
    }

    get news(): IReactiveNewsContentItem[] {
        return this.allContent.filter(c => c.is_news) as IReactiveNewsContentItem[];
    }

    get events(): IReactiveEventContentItem[] {
        return this.allContent.filter(c => c.is_event) as IReactiveEventContentItem[];
    }

    get files(): IReactiveFileContentItem[] {
        return this.allContent.filter(c => c.is_file || c.is_folder) as IReactiveFileContentItem[];
    }

    get media(): IReactiveMediaContentItem[] {
        return this.allContent.filter(c => c.is_media) as IReactiveMediaContentItem[];
    }

    setContentItems = (items: ContentItem[], clear = false) => {
        if (clear) {
            this.allContent = [];
        }
        this.allContent = [...this.allContent, ...items.map(item => {
            // @ts-ignore
            const contentItem: SchoolFeedContentItem = new ReactiveContentItem(item);
            return contentItem;
        })];
    }

    deleteItem = async (item, organization_id) => {
        const client = await WatsonApi();
        await client.apis.organizations.organizations_content_items_delete({
            id: item.id,
            organization_pk: organization_id,
        });
        this.allContent = this.allContent.filter(n => n.id !== item.id);
    }

    updateItem = (updatedItem) => {
        const itemToUpdate = this.allContent.find(c => c.id === updatedItem.id);
        if (itemToUpdate) {
            set(itemToUpdate, updatedItem);
        }
    }

    get isFetching(): boolean {
        return this.fetchStatus === "PENDING";
    }
}
